import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from '@/context/AppContext';
import { Toaster } from '@/components/ui/sonner';
import { AppShell } from '@/components/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { FileViewerHost } from '@/components/FileViewer';
import { Skeleton } from '@/components/ui/skeleton';

import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import InviteAccept from '@/pages/InviteAccept';
import Terms from '@/pages/legal/Terms';
import Privacy from '@/pages/legal/Privacy';
import HipaaNotice from '@/pages/legal/HipaaNotice';
import Baa from '@/pages/legal/Baa';
import PatientLink from '@/pages/PatientLink';
import PatientForm from '@/pages/PatientForm';
import PatientStatus from '@/pages/PatientStatus';
import PatientView from '@/pages/PatientView';
import DoctorFastSign from '@/pages/DoctorFastSign';
import NurseQueue from '@/pages/NurseQueue';
import NurseFastReview from '@/pages/NurseFastReview';
import NurseDetail from '@/pages/NurseDetail';
import Archive from '@/pages/Archive';
import DoctorQueue from '@/pages/DoctorQueue';
import DoctorReview from '@/pages/DoctorReview';
import IntakeRedirect from '@/pages/IntakeRedirect';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminCatalog from '@/pages/AdminCatalog';
import AdminUsers from '@/pages/AdminUsers';
import AdminBilling from '@/pages/AdminBilling';
import AdminSettings from '@/pages/AdminSettings';
import AdminAudit from '@/pages/AdminAudit';
import OwnerDashboard from '@/pages/OwnerDashboard';
import OwnerPractices from '@/pages/OwnerPractices';
import OwnerOrganisations from '@/pages/OwnerOrganisations';
import OwnerPracticeDetail from '@/pages/OwnerPracticeDetail';
import OwnerSupport from '@/pages/OwnerSupport';
import OwnerFeedback from '@/pages/OwnerFeedback';
import OwnerAudit from '@/pages/OwnerAudit';
import OwnerSettings from '@/pages/OwnerSettings';
import PatientRequestStart from '@/pages/PatientRequestStart';
import PatientRequestStatus from '@/pages/PatientRequestStatus';
import PatientRequests from '@/pages/PatientRequests';
import ClaimRequest from '@/pages/ClaimRequest';

function Guarded({ roles, children }) {
  return (
    <ProtectedRoute roles={roles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function NotFound() {
  return <div className="p-8"><h1 className="text-2xl font-semibold">Not found</h1><p className="text-muted-foreground">The page you are looking for does not exist.</p></div>;
}

function RoleHome() {
  const { user, authLoading } = useApp();
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-5xl mx-auto space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }
  if (user?.role === 'doctor') return <Navigate to="/doctor" replace />;
  if (user?.role === 'nurse') return <Navigate to="/nurse" replace />;
  if (user?.role === 'admin') return <Navigate to="/admin" replace />;
  if (user?.role === 'owner') return <Navigate to="/owner" replace />;
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppProvider>
          <Toaster richColors position="top-right" />
          {/* Catches a render crash so it is a page the user can act on and a
              bug report we receive, rather than a white screen (ADR 0004 step 2).
              Inside AppProvider so a report carries the session's JWT. */}
          <ErrorBoundary>
          {/* Every path below is mirrored in lib/errorContext.js — a bug report
              names the PATTERN, because `/p/:token` as a URL is a live magic link. */}
          <Routes>
            {/* Public */}
            <Route path="/" element={<RoleHome />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/invite/:token" element={<InviteAccept />} />

            {/* Legal (public, no auth — patients reach these from their own pages) */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/hipaa" element={<HipaaNotice />} />
            <Route path="/baa" element={<Baa />} />

            {/* Patient magic link routes (public, token-based) */}
            <Route path="/intake/:pid" element={<IntakeRedirect />} />
            <Route path="/p/:token" element={<PatientLink />} />
            <Route path="/p/:token/form" element={<PatientForm />} />
            <Route path="/p/:token/status" element={<PatientStatus />} />
            <Route path="/p/:token/document" element={<PatientView />} />
            {/* Patient-direct requests (ADR 0008): the patient starts, the office claims. */}
            <Route path="/start" element={<PatientRequestStart />} />
            <Route path="/r/:token" element={<PatientRequestStatus />} />
            <Route path="/claim/:token" element={<ClaimRequest />} />
            <Route path="/patient-requests" element={<Guarded roles={['nurse', 'admin']}><PatientRequests /></Guarded>} />

            {/* Nurse */}
            <Route path="/nurse" element={<Guarded roles={['nurse', 'admin']}><NurseQueue /></Guarded>} />
            <Route path="/nurse/fast-review" element={<Guarded roles={['nurse', 'admin']}><NurseFastReview /></Guarded>} />
            <Route path="/nurse/archive" element={<Guarded roles={['nurse', 'admin']}><Archive /></Guarded>} />
            <Route path="/nurse/submission/:sid" element={<Guarded roles={['nurse', 'admin']}><NurseDetail /></Guarded>} />

            {/* Doctor */}
            <Route path="/doctor" element={<Guarded roles={['doctor', 'admin']}><DoctorQueue /></Guarded>} />
            <Route path="/doctor/fast-sign" element={<Guarded roles={['doctor', 'admin']}><DoctorFastSign /></Guarded>} />
            <Route path="/doctor/archive" element={<Guarded roles={['doctor', 'admin']}><Archive /></Guarded>} />
            <Route path="/doctor/review/:sid" element={<Guarded roles={['doctor', 'admin']}><DoctorReview /></Guarded>} />

            {/* Admin */}
            <Route path="/admin" element={<Guarded roles={['admin']}><AdminDashboard /></Guarded>} />
            <Route path="/admin/catalog" element={<Guarded roles={['admin']}><AdminCatalog /></Guarded>} />
            <Route path="/admin/users" element={<Guarded roles={['admin']}><AdminUsers /></Guarded>} />
            <Route path="/admin/billing" element={<Guarded roles={['admin']}><AdminBilling /></Guarded>} />
            <Route path="/admin/settings" element={<Guarded roles={['admin']}><AdminSettings /></Guarded>} />
            <Route path="/admin/audit" element={<Guarded roles={['admin']}><AdminAudit /></Guarded>} />

            {/* Platform Owner */}
            <Route path="/owner" element={<Guarded roles={['owner']}><OwnerDashboard /></Guarded>} />
            <Route path="/owner/practices" element={<Guarded roles={['owner']}><OwnerPractices /></Guarded>} />
            <Route path="/owner/practices/:pid" element={<Guarded roles={['owner']}><OwnerPracticeDetail /></Guarded>} />
            <Route path="/owner/organisations" element={<Guarded roles={['owner']}><OwnerOrganisations /></Guarded>} />
            <Route path="/owner/support" element={<Guarded roles={['owner']}><OwnerSupport /></Guarded>} />
            <Route path="/owner/feedback" element={<Guarded roles={['owner']}><OwnerFeedback /></Guarded>} />
            <Route path="/owner/audit" element={<Guarded roles={['owner']}><OwnerAudit /></Guarded>} />
            <Route path="/owner/settings" element={<Guarded roles={['owner']}><OwnerSettings /></Guarded>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
          <FileViewerHost />
        </AppProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
